import type { TranslatedField } from 'admin-panel/constants/types';
import { sanitizeSourceHtmlForEmission } from 'admin-panel/utils/wysiwyg-editor/html';

export type PageHeaderImageBlock = {
  src: string;
  html: string;
};

export type PageHeaderImageTranslations = Partial<
  Record<string, PageHeaderImageBlock>
>;

export type PageHeaderImagePrefixTranslations = Partial<Record<string, string>>;

export type ParsedPageHeaderImage = {
  headerImage: PageHeaderImageBlock | null;
  prefixHtml: string;
  bodyHtml: string;
};

const KNOWN_HEADER_INSTRUCTION =
  'bitte fügen sie das gewünschte header-bild in die nachfolgende tabellenzelle ein';

const LEADING_IGNORABLE_PATTERN = /^(?:(?:\s+)|(?:<!--[\s\S]*?-->))*/;
const ALLOWED_HEADER_ELEMENTS = new Set([
  'A',
  'BR',
  'H1',
  'IMG',
  'P',
  'TABLE',
  'TBODY',
  'TD',
  'TFOOT',
  'TH',
  'THEAD',
  'TR',
]);
const ALLOWED_HEADER_ATTRIBUTES = new Set([
  'align',
  'alt',
  'bgcolor',
  'border',
  'cellpadding',
  'cellspacing',
  'class',
  'colspan',
  'decoding',
  'dir',
  'height',
  'id',
  'lang',
  'loading',
  'rowspan',
  'scope',
  'sizes',
  'src',
  'srcset',
  'style',
  'summary',
  'title',
  'valign',
  'width',
]);
const ALLOWED_LINK_ATTRIBUTES = new Set(['href', 'rel', 'target', 'title']);

function normalizeText(value: string) {
  return value
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/[:.!]+$/, '');
}

function isSafeImageSource(source: string | null): source is string {
  if (!source) return false;

  try {
    const url = new URL(source);

    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function isFullWidthImage(image: HTMLImageElement) {
  return (
    image.classList.contains('header') ||
    image.getAttribute('width')?.replace(/\s/g, '') === '100%' ||
    image.style.width.replace(/\s/g, '') === '100%'
  );
}

function meaningfulChildNodes(element: ParentNode) {
  return [...element.childNodes].filter((node) => {
    return !(
      node.nodeType === Node.COMMENT_NODE ||
      (node.nodeType === Node.TEXT_NODE && !node.textContent?.trim())
    );
  });
}

function parseSingleElement(html: string) {
  const template = document.createElement('template');
  template.innerHTML = html;

  const meaningfulNodes = meaningfulChildNodes(template.content);

  if (meaningfulNodes.length !== 1) return null;

  const [element] = meaningfulNodes;

  return element instanceof HTMLElement ? element : null;
}

function hasOnlyAllowedHeaderMarkup(element: HTMLElement) {
  return [element, ...element.querySelectorAll('*')].every((descendant) => {
    if (!ALLOWED_HEADER_ELEMENTS.has(descendant.tagName)) return false;
    const allowedAttributes =
      descendant.tagName === 'A'
        ? ALLOWED_LINK_ATTRIBUTES
        : ALLOWED_HEADER_ATTRIBUTES;

    return [...descendant.attributes].every(({ name }) => {
      const normalizedName = name.toLowerCase();

      return (
        allowedAttributes.has(normalizedName) ||
        normalizedName.startsWith('aria-') ||
        normalizedName.startsWith('data-')
      );
    });
  });
}

function isImageOnlyWrapper(element: HTMLElement, image: HTMLImageElement) {
  const meaningfulChildren = meaningfulChildNodes(element);

  if (meaningfulChildren.length !== 1) return false;
  if (meaningfulChildren[0] === image) return true;

  const child = meaningfulChildren[0];
  if (!(child instanceof HTMLAnchorElement)) return false;

  const anchorChildren = meaningfulChildNodes(child);

  return anchorChildren.length === 1 && anchorChildren[0] === image;
}

function isRecognizedHeaderTable(
  table: HTMLTableElement,
  image: HTMLImageElement
) {
  if (table.querySelectorAll('img').length !== 1) return false;
  if (table.querySelector('table, video, audio, iframe, object, embed')) {
    return false;
  }
  if (!isFullWidthImage(image)) return false;

  const textOnlyTable = table.cloneNode(true);
  if (!(textOnlyTable instanceof HTMLTableElement)) return false;

  textOnlyTable.querySelector('img')?.remove();

  const remainingText = normalizeText(textOnlyTable.textContent ?? '');
  const containsOnlyKnownInstruction =
    remainingText === '' || remainingText === KNOWN_HEADER_INSTRUCTION;

  if (!containsOnlyKnownInstruction) return false;

  return (
    table.getAttribute('summary')?.toLowerCase() === 'header' ||
    remainingText === KNOWN_HEADER_INSTRUCTION
  );
}

function recognizedHeaderImage(element: HTMLElement) {
  const imageCount =
    element instanceof HTMLImageElement
      ? 1
      : element.querySelectorAll('img').length;

  if (imageCount !== 1) return null;

  const image =
    element instanceof HTMLImageElement
      ? element
      : element.querySelector('img');

  if (!(image instanceof HTMLImageElement)) return null;
  const source = image.getAttribute('src');
  if (!isSafeImageSource(source)) return null;

  const links = element.querySelectorAll('a');
  if (links.length > 1 || (links[0] && !isImageOnlyWrapper(links[0], image))) {
    return null;
  }

  if (element instanceof HTMLTableElement) {
    return isRecognizedHeaderTable(element, image) ? image : null;
  }

  if (element instanceof HTMLImageElement) {
    return isFullWidthImage(image) ? image : null;
  }

  if (element.tagName !== 'P' && element.tagName !== 'H1') return null;

  return isFullWidthImage(image) && isImageOnlyWrapper(element, image)
    ? image
    : null;
}

function sanitizeRecognizedHeader(html: string) {
  const sanitizedHtml = sanitizeSourceHtmlForEmission(html);
  const element = parseSingleElement(sanitizedHtml);
  const image =
    element && hasOnlyAllowedHeaderMarkup(element)
      ? recognizedHeaderImage(element)
      : null;

  if (!element || !image) return null;

  image.removeAttribute('srcset');
  image.removeAttribute('sizes');

  return { element, image, html: element.outerHTML };
}

export function parsePageHeaderImage(html: string): ParsedPageHeaderImage {
  const prefix = html.match(LEADING_IGNORABLE_PATTERN)?.[0] ?? '';
  const content = html.slice(prefix.length);
  const template = document.createElement('template');
  template.innerHTML = content;

  const [candidate] = meaningfulChildNodes(template.content);

  if (!(candidate instanceof HTMLElement)) {
    return { headerImage: null, prefixHtml: '', bodyHtml: html };
  }

  const recognizedHeader = sanitizeRecognizedHeader(candidate.outerHTML);

  if (!recognizedHeader) {
    return { headerImage: null, prefixHtml: '', bodyHtml: html };
  }

  const source = recognizedHeader.image.getAttribute('src');
  if (!source) {
    return { headerImage: null, prefixHtml: '', bodyHtml: html };
  }

  const serializedCandidate = candidate.outerHTML;
  candidate.remove();
  const bodyHtml = content.startsWith(serializedCandidate)
    ? content.slice(serializedCandidate.length)
    : template.innerHTML;

  return {
    headerImage: {
      src: source,
      html: recognizedHeader.html,
    },
    prefixHtml: prefix,
    bodyHtml,
  };
}

export function createPageHeaderImage(
  source: string
): PageHeaderImageBlock | null {
  if (!isSafeImageSource(source)) return null;

  const table = document.createElement('table');
  table.style.width = '100%';
  table.setAttribute('summary', 'header');

  const body = document.createElement('tbody');
  const row = document.createElement('tr');
  const cell = document.createElement('td');
  cell.style.height = '250px';
  cell.style.overflow = 'hidden';
  cell.style.display = 'inline-block';
  cell.style.width = '100%';

  const image = document.createElement('img');
  image.className = 'header';
  image.setAttribute('src', source);
  image.setAttribute('alt', '');
  image.setAttribute('width', '100%');

  cell.append(image);
  row.append(cell);
  body.append(row);
  table.append(body);

  return { src: source, html: table.outerHTML };
}

export function replacePageHeaderImageSource(
  headerImage: PageHeaderImageBlock,
  source: string
): PageHeaderImageBlock | null {
  if (!isSafeImageSource(source)) return null;

  const recognizedHeader = sanitizeRecognizedHeader(headerImage.html);
  if (!recognizedHeader) return null;

  const { element, image } = recognizedHeader;

  image.setAttribute('src', source);
  image.removeAttribute('srcset');
  image.removeAttribute('sizes');

  return { src: source, html: element.outerHTML };
}

export function splitPageContentTranslations(translations: TranslatedField): {
  headerImageTranslations: PageHeaderImageTranslations;
  headerImagePrefixTranslations: PageHeaderImagePrefixTranslations;
  contentTranslations: TranslatedField;
} {
  const headerImageTranslations: PageHeaderImageTranslations = {};
  const headerImagePrefixTranslations: PageHeaderImagePrefixTranslations = {};
  const contentTranslations: TranslatedField = {};

  for (const [locale, content] of Object.entries(translations)) {
    if (content === null) {
      headerImagePrefixTranslations[locale] = '';
      contentTranslations[locale] = null;
      continue;
    }

    const { headerImage, prefixHtml, bodyHtml } = parsePageHeaderImage(content);
    if (headerImage) headerImageTranslations[locale] = headerImage;
    headerImagePrefixTranslations[locale] = prefixHtml;
    contentTranslations[locale] = bodyHtml;
  }

  return {
    headerImageTranslations,
    headerImagePrefixTranslations,
    contentTranslations,
  };
}

export function composePageContent(
  content: string | null | undefined,
  headerImage: PageHeaderImageBlock | null | undefined = null,
  prefixHtml = ''
) {
  return headerImage || prefixHtml
    ? `${prefixHtml}${headerImage?.html ?? ''}${content ?? ''}`
    : content ?? null;
}
